import React, { useState, useEffect } from 'react';
import { Search, Loader2, CheckCircle2, XCircle, FileText, ChevronLeft, ChevronRight } from 'lucide-react';
import PageHeader from './PageHeader';
import './QuestionBankSearch.css';
import api from '../api/axiosConfig';

export default function QuestionBankSearch() {
    const [query, setQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [totalItems, setTotalItems] = useState(0);

    // Debounce text queries logic
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedQuery(query);
            setCurrentPage(1);
        }, 400);
        return () => clearTimeout(timer);
    }, [query]);

    // Execute database keyword matching search loops via Axios
    useEffect(() => {
        const fetchSearchResults = async () => {
            if (debouncedQuery.trim().length < 2) {
                setQuestions([]);
                setTotalPages(0);
                setTotalItems(0);
                return;
            }

            setLoading(true);
            try {
                const response = await api.get('/admin/questions/search', {
                    params: { q: debouncedQuery, page: currentPage, limit: 10 }
                });

                if (response.data.success) {
                    setQuestions(response.data.questions);
                    setTotalPages(response.data.pagination.totalPages);
                    setTotalItems(response.data.pagination.totalItems);
                }
            } catch (error) {
                console.error('Search query runtime failure:', error.message);
            } finally {
                setLoading(false);
            }
        };

        fetchSearchResults();
    }, [debouncedQuery, currentPage]);

    return (
        <div className="search-component-wrapper">

            <PageHeader
                title="Question Bank Search"
                description="Search global database curriculum items, review question details, and audit correct choice parameters."
            />

            {/* 🔍 The core search input field structure */}
            <div className="search-input-wrapper">
                <Search className="search-icon-hint" size={20} />
                <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Type keyword terms to query the question bank..."
                    className="search-input-field"
                />
                {loading && <Loader2 className="search-spinner icon-spin" size={20} />}
            </div>

            {/* Summary Tracker bar element */}
            {debouncedQuery.trim().length >= 2 && !loading && (
                <div className="search-meta-summary">
                    Found <strong>{totalItems}</strong> matching questions for "{debouncedQuery}"
                </div>
            )}

            {/* 📜 Results lists panel feed layout */}
            <div className="search-results-feed">
                {questions.map((question) => {
                    // Dynamic defensive mapping for structural metadata paths
                    const gradeName = question.Topic?.Subject?.Grade?.name || 'N/A';
                    const subjectName = question.Topic?.Subject?.name || 'N/A';
                    const topicName = question.Topic?.name || 'N/A';

                    return (
                        <div key={question.id} className="question-result-card">

                            {/* 🌟 Top metadata tag chain context row */}
                            <div className='question-bc-container'>
                                <div className="card-context-breadcrumbs">
                                    <span className="breadcrumb-pill text-grade">{gradeName}</span>
                                    <span className="breadcrumb-separator">/</span>
                                    <span className="breadcrumb-pill text-subject">{subjectName}</span>
                                    <span className="breadcrumb-separator">/</span>
                                    <span className="breadcrumb-pill text-topic">{topicName}</span>
                                </div>

                                <div className="card-header-row">
                                    <span className="badge-type-pill">{question.type}</span>
                                    <span className="badge-points-pill">{question.points} Points</span>
                                </div>
                            </div>

                            <h3 className="question-statement-text">{question.text}</h3>

                            {question.Options && question.Options.length > 0 && (
                                <div className="options-grid-layout">
                                    {question.Options.map((option) => (
                                        <div
                                            key={option.id}
                                            className={`option-item-row ${option.isCorrect ? 'is-correct-answer' : ''}`}
                                        >
                                            {option.isCorrect ? (
                                                <CheckCircle2 size={16} className="text-success" />
                                            ) : (
                                                <XCircle size={16} className="text-muted-cross" />
                                            )}
                                            <span>{option.text}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}

                {debouncedQuery.trim().length >= 2 && questions.length === 0 && !loading && (
                    <div className="search-empty-state">
                        <FileText size={48} className="icon-muted" />
                        <h4>No matches discovered</h4>
                        <p>We couldn't find any questions matching those criteria in the MySQL index database.</p>
                    </div>
                )}
            </div>

            {/* Pagination component alignment bar */}
            {totalPages > 1 && !loading && (
                <div className="search-pagination-footer">
                    <button
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className="btn-page-nav"
                    >
                        <ChevronLeft size={16} />
                        <span>Prev</span>
                    </button>

                    <span className="page-indicator-label">
                        Page <strong>{currentPage}</strong> of {totalPages}
                    </span>

                    <button
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className="btn-page-nav"
                    >
                        <span>Next</span>
                        <ChevronRight size={16} />
                    </button>
                </div>
            )}
        </div>
    );
}